import React, { useState, useSyncExternalStore } from 'react';
import { getVersion, inspect, subscribe } from '@/lib/mnemonics/registry';
import { MOD_LABEL } from '@/lib/mnemonics/keys';

/**
 * Dev-only mnemonic inspector. Surfaces duplicate bindings during the rollout
 * so collisions are caught while writing the page rather than by a user finding
 * that Alt+S does the wrong thing. Compiled out of production builds.
 */
export default function MnemonicDevOverlay() {
  const [open, setOpen] = useState(false);
  useSyncExternalStore(subscribe, getVersion, getVersion);

  if (process.env.NODE_ENV === 'production') return null;

  const scopes = inspect();
  const conflicts = scopes.reduce((n, s) => n + s.duplicates.length, 0);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`fixed bottom-3 left-3 z-[200] rounded-full px-3 py-1.5 text-[11px] font-semibold shadow-lg ${
          conflicts ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-100'
        }`}
        title="Mnemonic inspector (dev only)"
      >
        {MOD_LABEL()} {conflicts ? `· ${conflicts} conflict${conflicts > 1 ? 's' : ''}` : 'ok'}
      </button>
    );
  }

  return (
    <div className="fixed bottom-3 left-3 z-[200] max-h-[70vh] w-80 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] text-slate-100 shadow-2xl">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold">Mnemonics ({MOD_LABEL()}+key)</span>
        <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white">
          close
        </button>
      </div>

      {scopes.length === 0 && <p className="text-slate-400">No scopes registered.</p>}

      {scopes.map((scope) => (
        <div key={scope.id} className="mb-3">
          <p className="mb-1 font-semibold text-slate-300">
            {scope.type}
            {scope.label ? ` · ${scope.label}` : ''}
          </p>
          {scope.entries.length === 0 && <p className="text-slate-500">— none —</p>}
          {scope.entries.map((entry) => (
            <div
              key={entry.char}
              className={`flex justify-between gap-2 ${entry.count > 1 ? 'text-red-400' : 'text-slate-200'}`}
            >
              <span className="font-mono uppercase">{entry.char}</span>
              <span className="truncate text-right">{entry.labels.join(' / ')}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
